// Smart Sash Admin Panel - Device Store
import { CreateDeviceRequest, DeviceDTO, PaginationMetadata, UpdateDeviceRequest } from '@/models';
import { deviceService } from '@/services/api/deviceService';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface DeviceState {
  devices: DeviceDTO[];
  selectedDevice: DeviceDTO | null;
  pagination: PaginationMetadata | null;
  isLoading: boolean;
  error: string | null;
  fetchDevices: (
    page?: number,
    pageSize?: number,
    institutionId?: string,
    labId?: string
  ) => Promise<void>;
  fetchDeviceById: (id: string) => Promise<void>;
  createDevice: (data: CreateDeviceRequest) => Promise<DeviceDTO>;
  updateDevice: (id: string, data: UpdateDeviceRequest) => Promise<void>;
  deleteDevice: (id: string) => Promise<void>;
  setSelectedDevice: (device: DeviceDTO | null) => void;
  clearError: () => void;
}

export const useDeviceStore = create<DeviceState>()(
  devtools(
    (set) => ({
      devices: [],
      selectedDevice: null,
      pagination: null,
      isLoading: false,
      error: null,
      fetchDevices: async (page = 1, pageSize = 10, institutionId?: string, labId?: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await deviceService.getAll({ page, pageSize, institutionId, labId });
          set({ devices: response.data, pagination: response.pagination, isLoading: false });
        } catch (error: unknown) {
          set({ error: getErrorMessage(error, 'Failed to fetch devices'), isLoading: false });
          throw error;
        }
      },
      fetchDeviceById: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          const device = await deviceService.getById(id);
          set({ selectedDevice: device, isLoading: false });
        } catch (error: unknown) {
          set({ error: getErrorMessage(error, 'Failed to fetch device'), isLoading: false });
          throw error;
        }
      },
      createDevice: async (data: CreateDeviceRequest) => {
        set({ isLoading: true, error: null });
        try {
          const newDevice = await deviceService.create(data);
          set((state) => ({ devices: [...state.devices, newDevice], isLoading: false }));
          return newDevice;
        } catch (error: unknown) {
          set({ error: getErrorMessage(error, 'Failed to create device'), isLoading: false });
          throw error;
        }
      },
      updateDevice: async (id: string, data: UpdateDeviceRequest) => {
        set({ isLoading: true, error: null });
        try {
          const updatedDevice = await deviceService.update(id, data);
          set((state) => ({
            devices: state.devices.map((d) => (d.id === id ? updatedDevice : d)),
            selectedDevice: state.selectedDevice?.id === id ? updatedDevice : state.selectedDevice,
            isLoading: false,
          }));
        } catch (error: unknown) {
          set({ error: getErrorMessage(error, 'Failed to update device'), isLoading: false });
          throw error;
        }
      },
      deleteDevice: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          await deviceService.delete(id);
          set((state) => ({
            devices: state.devices.filter((d) => d.id !== id),
            selectedDevice: state.selectedDevice?.id === id ? null : state.selectedDevice,
            isLoading: false,
          }));
        } catch (error: unknown) {
          set({ error: getErrorMessage(error, 'Failed to delete device'), isLoading: false });
          throw error;
        }
      },
      setSelectedDevice: (device: DeviceDTO | null) => set({ selectedDevice: device }),
      clearError: () => set({ error: null }),
    }),
    { name: 'DeviceStore' }
  )
);
